import React, { FC, useEffect, useState } from "react";

import { formatClock } from "apps/user/learner/lib/advent-door-mechanics";
import { useTranslation } from "lib/i18n/use-translation";

import {
  CompletedNote,
  CompletionError,
  MechanicProps,
  useCompletion,
} from "./shared";
import styles from "../../december-doors.module.scss";

const TICK_MS = 250;

interface TimerMechanicProps extends MechanicProps {
  minutes: number;
}

const TimerMechanic: FC<TimerMechanicProps> = ({
  door,
  minutes,
  onComplete,
}) => {
  const { t } = useTranslation(["decemberDoors"]);
  const { submit, isSaving, hasFailed } = useCompletion(onComplete);
  const totalSeconds = minutes * 60;
  // Counted against the clock, not by ticks, so a throttled background tab
  // still finishes on time.
  const [endsAt, setEndsAt] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());

  const remaining =
    endsAt === null
      ? totalSeconds
      : Math.max(0, Math.ceil((endsAt - now) / 1000));

  useEffect(() => {
    if (endsAt === null) return undefined;
    const interval = window.setInterval(() => setNow(Date.now()), TICK_MS);
    return () => window.clearInterval(interval);
  }, [endsAt]);

  useEffect(() => {
    if (endsAt === null || remaining > 0) return;
    setEndsAt(null);
    submit({ seconds: totalSeconds });
  }, [endsAt, remaining, submit, totalSeconds]);

  if (door.isCompleted) {
    return (
      <CompletedNote
        door={door}
        line={t("decemberDoors:mechanic.timer.finished")}
      />
    );
  }

  if (endsAt !== null) {
    return (
      <div className={styles.mechanic}>
        <p className={styles["mechanic-clock"]} role="timer">
          {t("decemberDoors:mechanic.timer.remaining", {
            time: formatClock(remaining),
          })}
        </p>
        <button
          type="button"
          className={styles["mechanic-secondary"]}
          onClick={() => setEndsAt(null)}
        >
          {t("decemberDoors:mechanic.timer.stop")}
        </button>
      </div>
    );
  }

  // A run that reached zero but could not be saved keeps its time: the
  // learner retries the save rather than sitting through it again.
  if (hasFailed || isSaving) {
    return (
      <div className={styles.mechanic}>
        <button
          type="button"
          className={styles["mechanic-primary"]}
          onClick={() => submit({ seconds: totalSeconds })}
          disabled={isSaving}
        >
          {t("decemberDoors:mechanic.markDone")}
        </button>
        <CompletionError show={hasFailed} />
      </div>
    );
  }

  return (
    <div className={styles.mechanic}>
      <button
        type="button"
        className={styles["mechanic-primary"]}
        onClick={() => {
          const startedAt = Date.now();
          setNow(startedAt);
          setEndsAt(startedAt + totalSeconds * 1000);
        }}
      >
        {t("decemberDoors:mechanic.timer.start", { minutes })}
      </button>
    </div>
  );
};

export default TimerMechanic;
